import { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import { useAuth, formatApiErrorDetail } from "../../auth";
import { useCurrency, useCurrencyBusy } from './CurrencyContext';
import { readDemoWallet, saveDemoWallet, toMinor } from "./credits";
import { useRevealedStats } from './useRevealedStats';
import { useWalletPresentation } from './useWalletPresentation';
import { waitForPresentation } from './waitForPresentation';
import { useGamePreferences } from './useGamePreferences';
import { withWalletTurn } from './walletTurns';
import { useKenoSound } from "./useKenoSound";
import { KENO_API as API, gameHeaders, loadFairness, rotateFairness } from "./fairnessApi";
import { useBalanceSync } from './useBalanceSync';
import { useOriginalsAccess } from '../originals/useOriginalsAccess';
import { DEFAULT_STRATEGY, parseStrategy, strategyNextBet, strategyReachedLimit } from '../originals/autoStrategy';
import { parseAutoBetCount } from '../originals/autoBetting';
import { clearSessionRecentBets } from '../originals/sessionRecentBets';

const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

export const useKeno = () => {
  const { user, loading: authLoading } = useAuth();
  const access = useOriginalsAccess(user);
  const [config, setConfig] = useState(null);
  const [wallet, setWallet] = useState({ balance_minor: 0, revision: 0, rounds: [], ghost: true });
  const walletRef = useRef(wallet);
  const [walletLoading, setWalletLoading] = useState(true);
  const [fairness, setFairness] = useState(null);
  const fairnessRef = useRef(null);
  const [selected, setSelected] = useState([]);
  const [picking, setPicking] = useState(false);
  const pickingRef = useRef(false);
  const [bet, setBet] = useState("1.00");
  const [risk, setRisk] = useState("medium");
  const [mode, setMode] = useState("manual");
  const [count, setCount] = useState("0");
  const [remaining, setRemaining] = useState(0);
  const [advanced, setAdvanced] = useState(() => ({ ...DEFAULT_STRATEGY }));
  const [auto, setAuto] = useState(false);
  const [busy, setBusy] = useState(false);
  const [drawn, setDrawn] = useState([]);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");
  const { instant, turbo, muted, volume, setInstant, setTurbo, setMuted, setVolume, settingsControls, beginRound, getPreferences } = useGamePreferences(user?.id || 'demo', auto, busy);
  const [ghostBusy, setGhostBusy] = useState(false);
  const sound = useKenoSound(muted, volume, 'keno');
  const epoch = useRef(0);
  const autoRef = useRef(false);
  const autoRunId = useRef(0);
  const busyRef = useRef(false);
  const turnPending = useRef(false);
  const demo = !user;
  const currency = useCurrency();
  const owner = user ? user.id + (currency.accountDemo ? ':account-demo' : '') : 'demo';
  const { stageRound, revealRound } = useRevealedStats(owner, 'keno');
  const { reserveStake, confirmWallet, revealPayout, cancelStake, presentWallet } = useWalletPresentation(owner, 'keno');
  const applyWallet = useCallback((value) => { walletRef.current = value; setWallet(value); }, []);
  useBalanceSync({ owner, demo, walletRef, apply: applyWallet, isBusy: () => busyRef.current || autoRef.current });
  const applyFairness = useCallback((value, force = false) => {
    const previous = fairnessRef.current;
    if (force || !previous || (value.seed_id === previous.seed_id && value.nonce >= previous.nonce) || value.created_at > previous.created_at) {
      fairnessRef.current = value; setFairness(value);
    }
  }, []);
  const stop = useCallback(() => { autoRunId.current += 1; autoRef.current = false; setAuto(false); setRemaining(0); }, []);
  useEffect(() => {
    if (authLoading) return;
    let active = true;
    axios.get(`${API}/keno/config`, { headers: gameHeaders(demo) }).then(({ data }) => { if (active) setConfig(data); })
      .catch(() => { if (active) setError("Unable to load Keno. Please reload to try again."); });
    return () => { active = false; };
  }, [authLoading, demo, owner]);

  useEffect(() => {
    const version = ++epoch.current;
    autoRef.current = false; busyRef.current = false; pickingRef.current = false; fairnessRef.current = null;
    setAuto(false); setBusy(false); setPicking(false); setFairness(null); setSelected([]); setDrawn([]); setResult(null); setRemaining(0); setError("");
    setWalletLoading(true);
    if (authLoading) return;
    let initialLoad = true;
    const load = async () => {
      if (busyRef.current || autoRef.current) return;
      try {
        const [value, seed] = await Promise.all([user ? axios.get(`${API}/keno/wallet`, { headers: gameHeaders(false) }).then((r) => r.data) : Promise.resolve(readDemoWallet()), loadFairness(!user)]);
        if (version === epoch.current) {
          if (initialLoad || value.revision >= walletRef.current.revision) applyWallet(value);
          applyFairness(seed, initialLoad); initialLoad = false; setWalletLoading(false);
        }
      } catch { if (version === epoch.current) { setError("Unable to load your balance or fairness commitment. Please try again."); setWalletLoading(false); } }
    };
    load();
    const timer = setInterval(load, 5000);
    return () => { epoch.current = version + 1; autoRef.current = false; clearInterval(timer); };
  }, [owner, authLoading, applyWallet, applyFairness]); // eslint-disable-line react-hooks/exhaustive-deps

  const toggle = (n) => {
    if (busyRef.current || autoRef.current || pickingRef.current) return;
    setError(""); setDrawn([]); setResult(null); sound("pick", n % 8);
    setSelected((values) => values.includes(n) ? values.filter((value) => value !== n) : values.length < 10 ? [...values, n].sort((a, b) => a - b) : values);
  };
  const randomPick = async () => {
    if (busyRef.current || autoRef.current || pickingRef.current) return;
    const version = epoch.current;
    const picks = new Set();
    while (picks.size < (selected.length || 10)) picks.add((crypto.getRandomValues(new Uint32Array(1))[0] % 40) + 1);
    setSelected([]); setDrawn([]); setResult(null); setError(""); pickingRef.current = true; setPicking(true);
    const next = [];
    for (const n of picks) {
      if (version !== epoch.current) return;
      next.push(n); setSelected([...next]); sound("pick", next.length);
      if (!window.matchMedia("(prefers-reduced-motion: reduce)").matches) await wait(38);
    }
    if (version === epoch.current) { pickingRef.current = false; setPicking(false); }
  };
  const clear = () => { if (!busyRef.current && !autoRef.current && !pickingRef.current) { setSelected([]); setDrawn([]); setResult(null); setError(""); } };

  const refreshFairness = async () => { const version = epoch.current; const seed = await loadFairness(demo); if (version === epoch.current) applyFairness(seed); };
  const rotateSeed = async (clientSeed) => {
    if (busyRef.current || autoRef.current || pickingRef.current) throw new Error("Wait for the current action to finish.");
    const version = epoch.current;
    const value = await rotateFairness(demo, fairnessRef.current, clientSeed);
    if (version === epoch.current) applyFairness(value.current);
    return value;
  };
  const updateGhost = async (ghost) => {
    if (demo || ghostBusy) return;
    const version = epoch.current; setGhostBusy(true);
    try { await axios.put(`${API}/keno/preferences`, { ghost }, { headers: gameHeaders(false) }); if (version === epoch.current) applyWallet({ ...walletRef.current, ghost }); }
    catch { if (version === epoch.current) setError("Unable to update Ghost mode."); }
    finally { if (version === epoch.current) setGhostBusy(false); }
  };

  const play = async (override = null) => {
    if (turnPending.current) return null;
    turnPending.current = true;
    const current = epoch.current;
    try { return await withWalletTurn(owner, () => current === epoch.current && (override === null || autoRef.current) ? playNow(override) : null); }
    finally { turnPending.current = false; }
  };
  const playNow = async (override = null) => {
    if (access.blocked || access.loading || access.error) return null;
    const minor = override ?? toMinor(bet);
    if (busyRef.current || pickingRef.current || authLoading || walletLoading || !fairnessRef.current) return null;
    if (!minor || minor > (config?.max_bet_minor || 0) || !selected.length) { setError("Choose numbers and enter a valid bet amount."); return null; }
    if (minor > walletRef.current.balance_minor) { setError("Insufficient V balance."); return null; }
    const { instant, turbo } = beginRound();
    const version = epoch.current;
    busyRef.current = true; setBusy(true); setError(""); setDrawn([]); setResult(null); sound("bet");
    const commitment = fairnessRef.current;
    const payload = { request_id: crypto.randomUUID(), picks: selected, risk, bet_minor: minor, revision: walletRef.current.revision,
      client_seed: commitment.client_seed, seed_id: commitment.seed_id, nonce: commitment.nonce, server_seed_hash: commitment.server_seed_hash, paytable_version: config.paytable_version };
    reserveStake(payload.request_id, minor);
    try {
      const request = () => axios.post(`${API}/keno/${demo ? "demo" : "bet"}`, payload, { headers: gameHeaders(demo) });
      let response;
      try { response = await request(); } catch (failure) { if (failure.response) throw failure; response = await request(); }
      const round = response.data.round;
      if (round.server_seed_hash !== commitment.server_seed_hash || round.seed_id !== commitment.seed_id || round.nonce !== commitment.nonce) throw new Error("Round commitment mismatch. Play stopped.");
      stageRound(round);
      if (version !== epoch.current) return null;
      confirmWallet(payload.request_id, round);
      const value = { ...walletRef.current, balance_minor: demo ? walletRef.current.balance_minor - minor + round.payout_minor : response.data.balance_minor,
        revision: demo ? walletRef.current.revision + 1 : response.data.revision, rounds: [...walletRef.current.rounds.filter((r) => r.request_id !== round.request_id), round].slice(-50) };
      applyWallet(value); applyFairness({ ...commitment, nonce: round.nonce + 1 });
      if (demo) saveDemoWallet(value);
      if (instant || window.matchMedia("(prefers-reduced-motion: reduce)").matches) setDrawn(round.drawn);
      else {
        let hitCount = 0;
        for (let index = 0; index < round.drawn.length; index++) {
          if (version !== epoch.current) return null;
          const hit = round.picks.includes(round.drawn[index]);
          setDrawn(round.drawn.slice(0, index + 1)); sound(hit ? "hit" : "draw", hit ? hitCount++ : index);
          await wait(turbo ? 28 : 85);
        }
      }
      if (version === epoch.current) {
        setResult(round);
        sound(round.payout_minor > round.bet_minor ? "win" : round.payout_minor < round.bet_minor ? "loss" : "hit");
        await waitForPresentation('.keno-stage', instant);
        if (version === epoch.current) { revealPayout(round); revealRound(round); }
      }
      return round;
    } catch (failure) {
      cancelStake(payload.request_id);
      if (version === epoch.current) {
        setError(failure.response?.data?.detail ? formatApiErrorDetail(failure.response.data.detail) : failure.message === "Round commitment mismatch. Play stopped." ? failure.message : "Connection interrupted. Check your latest bets before trying again.");
        try {
          await refreshFairness();
          if (failure.response?.status === 409) { const { data } = await axios.get(`${API}/keno/config`, { headers: gameHeaders(demo) }); if (version === epoch.current) setConfig(data); }
          if (!demo) { const { data } = await axios.get(`${API}/keno/wallet`, { headers: gameHeaders(false) }); if (version === epoch.current) applyWallet(data); }
        } catch { /* Preserve the last confirmed state. */ }
      }
      return null;
    } finally { if (version === epoch.current) { busyRef.current = false; setBusy(false); } }
  };
  const start = async () => {
    if (autoRef.current) { stop(); return; }
    if (busyRef.current || pickingRef.current) return;
    if (mode === "manual") { await play(); return; }
    let rounds;
    try { rounds = parseAutoBetCount(count); } catch (failure) { setError(failure.message); return; }
    let strategy;
    try { strategy = parseStrategy(advanced); } catch (failure) { setError(failure.message); return; }
    const baseBet = toMinor(bet);
    let nextBet = baseBet, net = 0;
    const version = epoch.current;
    const runId = ++autoRunId.current;
    autoRef.current = true; setAuto(true);
    for (let i = 0; i < rounds && autoRef.current && version === epoch.current && runId === autoRunId.current; i++) {
      setRemaining(rounds === Infinity ? null : rounds - i);
      if (!nextBet || nextBet > config.max_bet_minor || nextBet > walletRef.current.balance_minor) { setError('Auto stopped: the next wager exceeds your balance or bet limit.'); break; }
      setBet((nextBet / 100).toFixed(2));
      const round = await play(nextBet);
      if (runId !== autoRunId.current || version !== epoch.current) return;
      if (!round) break;
      net += round.payout_minor - round.bet_minor;
      if (strategyReachedLimit(strategy, net)) break;
      nextBet = strategyNextBet(strategy, nextBet, baseBet, { ...round, win: round.payout_minor > round.bet_minor });
      const speed = getPreferences();
      await wait(speed.instant ? 65 : speed.turbo ? 130 : 400);
    }
    if (version === epoch.current && runId === autoRunId.current) stop();
  };
  const resetDemo = () => { if (demo && !busy && !auto && !picking) { const fresh = { balance_minor: 100000, revision: walletRef.current.revision, rounds: [] }; clearSessionRecentBets(owner); saveDemoWallet(fresh); applyWallet(fresh); setResult(null); setDrawn([]); setError(""); } };
  useCurrencyBusy(owner, busy || auto || picking);
  return { gameType: 'keno', access, config, wallet: { ...wallet, currency: currency.accountDemo ? 'demo' : 'v' }, walletLoading: walletLoading || authLoading || !currency.ready, demo, owner, selected, picking, toggle, randomPick, clear, bet, setBet,
    risk, setRisk, mode, setMode, count, setCount, remaining, auto, busy, start, stop, advanced, setAdvanced,
    drawn, result, error, displayWallet: { ...presentWallet(wallet), currency: currency.accountDemo ? 'demo' : 'v' }, instant, setInstant, turbo, setTurbo, muted, setMuted, volume, setVolume, settingsControls, resetDemo, fairness, refreshFairness, rotateSeed, updateGhost, ghostBusy,
    syncWallet: (next) => { if (next.revision >= walletRef.current.revision) applyWallet(next); } };
};